import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import {
  searchShroffPayment,
  confirmShroffPayment,
  listShroffPaymentHistory,
} from "../../services/shroffPaymentService";
import { getManualPaymentReceiptFile } from "../../services/paymentService";
import {
  TOKENS,
  Icon,
  ICONS,
  EmptyState,
  Spinner,
  Pagination,
  DEFAULT_PAGE_SIZE,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
} from "../../components/common/DashboardUI";

// Shroff counter payment desk — look up ANY payable by its payment reference
// (booking, member/club registration, membership/club renewal, ID card
// reprint) and record a payment taken in person, through the unified
// /api/shroff/payments/{search,confirm} routes (shroffPaymentService.js).
// Exported as two standalone pages, each its own ADMIN_NAV sidebar entry
// (config/adminNav.js): ShroffFindConfirmPage and ShroffPaymentHistoryPage.
// The confirm form is gated here by "confirm_shroff_payments".
//
// No receipt-file upload on this page, on purpose: the backend makes the
// scanned receipt optional for "confirm_shroff_payments" holders
// (ManualPaymentReceiptFile::rulesFor), which is exactly who can see the
// form. A caller with only "submit_manual_payment_receipts" would still have
// the file required, so the form isn't offered to them — they keep using the
// per-module Confirm Payment tabs, which do have the upload.
//
// The "Payment History" page lists every desk confirmation
// (GET /api/shroff/payments/history), gated by its own
// "view_shroff_payment_history" permission — independent of the search/
// confirm permissions, which is why it's a separate sidebar entry.

const PAYMENT_TYPE_LABELS = {
  booking: "Facility Booking",
  member_registration: "Member Registration",
  club_registration: "Club Registration",
  membership_renewal: "Membership Renewal",
  club_renewal: "Club Renewal",
  id_card_reprint: "ID Card Reprint",
};

function errInfo(err, fallback) {
  const data = err?.response?.data;
  return { message: data?.message || fallback, fields: data?.errors || {} };
}

const money = (v) => (v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`);

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

// Every payment_type returns a different row shape from the search route —
// normalize the bits this page displays into one { title, subtitle, fields }.
function describePayment(p) {
  switch (p.payment_type) {
    case "booking": {
      const entries = p.booking?.entries || [];
      const first = entries[0];
      return {
        title: p.payer?.full_name || p.member_code || "Booking payment",
        subtitle: p.payer?.email,
        fields: [
          ["Member code", p.member_code],
          ["Facility", p.booking?.facility_name],
          ["Booking", first ? `${first.booking_date} ${first.start_time}–${first.end_time}` : null],
          ["Entries", entries.length > 1 ? String(entries.length) : null],
          ["Phone", p.payer?.primary_phone_number],
          ["Staff-created", p.created_by_staff ? "Yes" : "No"],
        ],
      };
    }
    case "member_registration":
      return {
        title: [p.applicant?.title, p.applicant?.full_name].filter(Boolean).join(" ") || "Member registration",
        subtitle: p.applicant?.email,
        fields: [
          ["Requested club", p.applicant?.requested_club_name],
          ["NIC", p.applicant?.nic_number],
          ["Phone", p.applicant?.primary_phone_number],
          ["Process status", p.process_status],
        ],
      };
    case "club_registration":
      return {
        title: p.club?.club_name || "Club registration",
        subtitle: p.club?.reg_no,
        fields: [
          ["Club code", p.club_id ? p.club_code : null],
          ["Phone", p.club?.primary_phone_number],
          ["Coaches", p.club?.no_of_coaches],
          ["Process status", p.process_status],
        ],
      };
    case "club_renewal":
      return {
        title: p.club_name || p.club_code || "Club renewal",
        subtitle: p.club_code,
        fields: [],
      };
    case "membership_renewal":
    case "id_card_reprint":
    default:
      return {
        title: p.payer_name || p.member_code || "Payment",
        subtitle: p.email,
        fields: [["Member code", p.member_code]],
      };
  }
}

function DetailGrid({ fields }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      {fields
        .filter(([, v]) => v !== null && v !== undefined && v !== "")
        .map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs text-gray-400">{label}</dt>
            <dd className="text-sm font-medium break-words" style={{ color: TOKENS.NAVY }}>
              {value}
            </dd>
          </div>
        ))}
    </dl>
  );
}

// Icon paths only this page needs (heroicons-outline), kept local rather than
// growing DashboardUI's shared ICONS map.
const DESK_ICONS = {
  arrowLeft: "M10 19l-7-7m0 0l7-7m-7 7h18",
  document: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  copy: "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z",
  money: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  receipt: "M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z",
  cash: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z",
  info: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  pencil: "M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z",
  calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
};

// Round soft-blue icon badge used beside each card heading and detail field.
function IconBadge({ path, size = "lg" }) {
  const box = size === "lg" ? "w-14 h-14" : "w-8 h-8";
  const icon = size === "lg" ? "w-7 h-7" : "w-4 h-4";
  return (
    <span className={`${box} shrink-0 rounded-full flex items-center justify-center bg-blue-50 text-blue-600`}>
      <Icon path={path} className={icon} width={size === "lg" ? 1.8 : 2} />
    </span>
  );
}

function CardHeading({ icon, title, subtitle, right }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-4 min-w-0">
        <IconBadge path={icon} />
        <div className="min-w-0">
          <h3 className="font-bold text-lg" style={{ color: TOKENS.NAVY }}>
            {title}
          </h3>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

// Amber "Pending" / green "Paid" badge with a leading icon, as in the mockup.
function StatusBadge({ status }) {
  const pending = !status || status === "Pending";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold ${
        pending ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-700"
      }`}
    >
      <Icon path={pending ? ICONS.clock : ICONS.check} className="w-4 h-4" width={2} />
      {status || "Pending"}
    </span>
  );
}

function DetailItem({ icon, label, value, action }) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <IconBadge path={icon} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-semibold break-words" style={{ color: TOKENS.NAVY }}>
          {value || "—"}
        </p>
      </div>
      {action}
    </div>
  );
}

const Required = () => <span className="text-red-500 ml-0.5">*</span>;

function ShroffDesk() {
  const { can } = useAuth();
  const canConfirm = can("confirm_shroff_payments");

  const [reference, setReference] = useState("");
  const [searching, setSearching] = useState(false);
  const [payment, setPayment] = useState(null);
  const [searchError, setSearchError] = useState("");

  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [receiptReference, setReceiptReference] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmFieldErrors, setConfirmFieldErrors] = useState({});
  const [confirmResult, setConfirmResult] = useState(null);

  const fieldError = (key) => {
    const msgs = confirmFieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const resetConfirmForm = (p) => {
    // Left blank on purpose — the operator types the amount actually received.
    setPaidAmount("");
    setPaidDate(todayStr());
    setReceiptReference("");
    setNote("");
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirmResult(null);
  };

  const runSearch = async (ref) => {
    setSearching(true);
    setSearchError("");
    try {
      const found = await searchShroffPayment(ref);
      setPayment(found);
      return found;
    } catch (err) {
      setPayment(null);
      setSearchError(errInfo(err, "No payment found for that reference.").message);
      return null;
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const ref = reference.trim();
    if (!ref || searching) return;
    setPayment(null);
    const found = await runSearch(ref);
    if (found) resetConfirmForm(found);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (confirming || !payment) return;
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirming(true);
    try {
      const result = await confirmShroffPayment({
        payment_reference: payment.payment_reference,
        paid_amount: paidAmount,
        paid_date: paidDate,
        receipt_reference: receiptReference.trim(),
        note: note.trim() || undefined,
      });
      toast.success(result.message || "Payment confirmed.");
      setConfirmResult(result);
      // Re-fetch so the status pill/amounts reflect the now-completed payment.
      await runSearch(payment.payment_reference);
    } catch (err) {
      const info = errInfo(err, "Could not confirm this payment.");
      setConfirmError(info.message);
      setConfirmFieldErrors(info.fields);
    } finally {
      setConfirming(false);
    }
  };

  const handleClear = () => {
    setReference("");
    setPayment(null);
    setSearchError("");
    resetConfirmForm(null);
  };

  // Renewal/reprint search rows don't always carry a status description, so
  // fall back to paid_at rather than trusting payment_status alone.
  const isPending =
    payment && !payment.payment_completed && !payment.paid_at && (payment.payment_status ?? "Pending") === "Pending";
  const described = payment ? describePayment(payment) : null;
  const statusLabel = payment ? payment.payment_status || (payment.paid_at ? "Paid" : "Pending") : null;

  // Member code / club code shown under the payer name and in its own tile;
  // the remaining type-specific fields go into the "more details" row.
  const payerCode = payment ? payment.member_code || payment.club_code || described.subtitle : null;
  const extraFields = payment
    ? [
        [
          payment.payment_type === "club_registration" ? "Registration no." : "Email",
          described.subtitle !== payerCode ? described.subtitle : null,
        ],
        ["Paid amount", payment.paid_at || Number(payment.paid_amount) > 0 ? money(payment.paid_amount) : null],
        ["Paid at", payment.paid_at ? fmtDateTime(payment.paid_at) : null],
        ...described.fields.filter(([label]) => label !== "Member code" && label !== "Club code"),
      ]
    : [];
  const hasExtra = extraFields.some(([, v]) => v !== null && v !== undefined && v !== "");

  const copyReference = () => {
    if (!payment?.payment_reference) return;
    navigator.clipboard
      ?.writeText(String(payment.payment_reference))
      .then(() => toast.success("Payment reference copied."))
      .catch(() => {});
  };

  const cardClass = "bg-white/80 rounded-2xl shadow-sm border border-blue-50 p-5 sm:p-6";
  const errorText = (key) => fieldError(key) && <p className="text-xs text-red-600 mt-1">{fieldError(key)}</p>;

  return (
    <div className="space-y-5">
      {/* ── Search Payment ─────────────────────────────────────────────── */}
      <div className={cardClass}>
        <CardHeading
          icon={ICONS.search}
          title="Search Payment"
          subtitle="Enter the payment reference number to find the payment details."
        />
        <form onSubmit={handleSearch} className="mt-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 pointer-events-none">
              <Icon path={DESK_ICONS.document} className="w-5 h-5" />
            </span>
            <input
              className={`${inputClass} pl-11 py-3`}
              placeholder="Enter payment reference number"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={searching || !reference.trim()}
            className={`${btnPrimary} sm:w-48 py-3 bg-blue-600 hover:bg-blue-700`}
          >
            <Icon path={ICONS.search} className="w-5 h-5" width={2} />
            {searching ? "Searching…" : "Search"}
          </button>
          <button type="button" onClick={handleClear} className={`${btnSecondary} sm:w-48 py-3 bg-white`}>
            <Icon path={ICONS.refresh} className="w-5 h-5" />
            Clear
          </button>
        </form>
        {searchError && <p className="text-sm text-red-600 mt-3">{searchError}</p>}
      </div>

      {payment && (
        <>
          {/* ── Payment Details ──────────────────────────────────────────── */}
          <div className={cardClass}>
            <CardHeading
              icon={ICONS.card}
              title="Payment Details"
              subtitle="Member and payment information"
              right={<StatusBadge status={statusLabel} />}
            />

            <div className="mt-5 rounded-xl border border-gray-100 bg-white grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]">
              {/* Payer */}
              <div className="flex items-center gap-4 p-5 border-b lg:border-b-0 lg:border-r border-gray-100 min-w-0">
                <span className="w-16 h-16 shrink-0 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                  <Icon path={ICONS.user} className="w-8 h-8" width={2} />
                </span>
                <div className="min-w-0">
                  <p className="text-lg font-bold truncate" style={{ color: TOKENS.NAVY }}>
                    {described.title}
                  </p>
                  {payerCode && <p className="text-sm text-gray-500 truncate">{payerCode}</p>}
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500">
                    <Icon path={ICONS.users} className="w-4 h-4" />
                    {PAYMENT_TYPE_LABELS[payment.payment_type] || "Payment"}
                  </p>
                </div>
              </div>

              {/* Key fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 p-5 gap-y-5 sm:gap-y-0 border-b lg:border-b-0 lg:border-r border-gray-100">
                <div className="sm:pr-5 sm:pb-4 sm:border-r sm:border-b border-gray-100">
                  <DetailItem
                    icon={DESK_ICONS.document}
                    label="Payment Reference"
                    value={payment.payment_reference}
                    action={
                      <button
                        type="button"
                        onClick={copyReference}
                        title="Copy reference"
                        className="text-gray-400 hover:text-blue-600 transition self-center"
                      >
                        <Icon path={DESK_ICONS.copy} className="w-4 h-4" />
                      </button>
                    }
                  />
                </div>
                <div className="sm:pl-5 sm:pb-4 sm:border-b border-gray-100">
                  <DetailItem
                    icon={ICONS.users}
                    label={payment.club_code && !payment.member_code ? "Club Code" : "Member Code"}
                    value={payment.member_code || payment.club_code}
                  />
                </div>
                <div className="sm:pr-5 sm:pt-4 sm:border-r border-gray-100">
                  <DetailItem icon={DESK_ICONS.money} label="Payable Amount" value={money(payment.payable_amount)} />
                </div>
                <div className="sm:pl-5 sm:pt-4">
                  <DetailItem icon={DESK_ICONS.receipt} label="Income" value={payment.income_description} />
                </div>
              </div>

              {/* Status */}
              <div className="p-5 flex lg:flex-col lg:justify-center items-center lg:items-start gap-3 lg:min-w-[180px]">
                <p className="text-sm text-gray-500">Status</p>
                <StatusBadge status={statusLabel} />
              </div>
            </div>

            {hasExtra && (
              <div className="mt-4 rounded-xl bg-blue-50/40 border border-gray-100 p-4">
                <DetailGrid fields={extraFields} />
              </div>
            )}
          </div>

          {/* ── Confirm Counter Payment ──────────────────────────────────── */}
          <div className={cardClass}>
            <CardHeading
              icon={DESK_ICONS.cash}
              title="Confirm Counter Payment"
              subtitle="Enter the payment details received at the shroff counter."
            />

            {confirmResult ? (
              <div className="mt-5 rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
                {confirmResult.message || "Payment confirmed."}
              </div>
            ) : !isPending ? (
              <p className="mt-5 text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
                This payment is already <strong>{payment.payment_status || "completed"}</strong> — nothing to confirm.
              </p>
            ) : !canConfirm ? (
              <p className="mt-5 text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
                You don't have permission to confirm this payment.
              </p>
            ) : (
              <form onSubmit={handleConfirm} className="mt-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <label className={labelClass}>
                      Paid Amount
                      <Required />
                    </label>
                    <div className="flex rounded-lg border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition overflow-hidden">
                      <span className="px-4 flex items-center text-sm text-gray-500 bg-gray-50 border-r border-gray-200">
                        LKR
                      </span>
                      {/* Left blank on purpose — the operator types the amount actually received. */}
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="flex-1 min-w-0 px-3 py-2.5 text-sm text-gray-700 focus:outline-none"
                        placeholder="Enter amount"
                        value={paidAmount}
                        onChange={(e) => setPaidAmount(e.target.value)}
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Must match the payable amount.</p>
                    {errorText("paid_amount")}
                  </div>
                  <div>
                    <label className={labelClass}>
                      Paid Date
                      <Required />
                    </label>
                    <input
                      type="date"
                      className={inputClass}
                      value={paidDate}
                      max={todayStr()}
                      onChange={(e) => setPaidDate(e.target.value)}
                      required
                    />
                    {errorText("paid_date")}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>
                    Receipt Reference
                    <Required />
                  </label>
                  <div className="relative">
                    <input
                      className={`${inputClass} pr-11`}
                      placeholder="e.g. SHROFF-001"
                      value={receiptReference}
                      onChange={(e) => setReceiptReference(e.target.value)}
                      maxLength={255}
                      required
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 pointer-events-none">
                      <Icon path={DESK_ICONS.document} className="w-5 h-5" />
                    </span>
                  </div>
                  {errorText("receipt_reference")}
                </div>

                <div>
                  <label className={labelClass}>
                    Note <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <textarea
                      className={`${inputClass} resize-none pr-11`}
                      rows={2}
                      maxLength={1000}
                      placeholder="Add any additional notes..."
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <span className="absolute bottom-3 right-3 text-gray-400 pointer-events-none">
                      <Icon path={DESK_ICONS.pencil} className="w-4 h-4" />
                    </span>
                  </div>
                  {errorText("note")}
                </div>

                {confirmError && <p className="text-sm text-red-600">{confirmError}</p>}

                <div className="flex flex-col md:flex-row gap-4 md:items-stretch">
                  <div className="flex-1 flex items-center gap-3 rounded-lg bg-blue-50/60 px-4 py-3">
                    <span className="w-6 h-6 shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center">
                      <Icon path={DESK_ICONS.info} className="w-4 h-4" width={2} />
                    </span>
                    <p className="text-xs text-gray-600">
                      Make sure the amount matches the payable amount and the receipt reference is correct.
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={confirming || !receiptReference.trim() || paidAmount === ""}
                    className={`${btnPrimary} md:w-96 py-3 bg-blue-600 hover:bg-blue-700`}
                  >
                    <Icon path={ICONS.check} className="w-5 h-5" width={2} />
                    {confirming ? "Confirming…" : "Confirm Payment"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Payment History ─────────────────────────────────────────────────────────
// Server-side paginated list of every desk confirmation (manual_payment_receipts
// rows, same shape as the Manual Approvals tab). payable_type filter values
// are the raw backend ones; each row's payment_for is the short form from
// ManualPaymentApprovalController::paymentFor().

const HISTORY_PAYABLE_TYPES = [
  ["booking_payment", "Facility Booking"],
  ["member_registration_process", "Member Registration"],
  ["club_registration_process", "Club Registration"],
  ["membership_renewal_process", "Membership Renewal"],
  ["club_renewal_process", "Club Renewal"],
  ["id_card_reprint_requests", "ID Card Reprint"],
];

const HISTORY_TYPE_LABELS = { ...PAYMENT_TYPE_LABELS, facility_booking: "Facility Booking" };

const DATE_MODES = [
  ["all", "All dates"],
  ["date", "Single date"],
  ["range", "Date range"],
];

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");

// Opens a blob in a pre-opened tab (window.open'd synchronously inside the
// click so the popup blocker allows it) — same as PaymentsManagement.jsx.
function openBlobInTab(blob, tab) {
  const url = URL.createObjectURL(blob);
  if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function ShroffPaymentHistory() {
  const { can } = useAuth();
  // GET /api/manual-payment-approvals/{id}/receipt's own gate.
  const canViewReceipt = can("view_manual_payment_approvals") || can("submit_manual_payment_receipts");

  const [dateMode, setDateMode] = useState("all");
  const [date, setDate] = useState(todayStr());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [payableType, setPayableType] = useState("");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewingReceiptId, setViewingReceiptId] = useState(null);

  // Every filter change goes back to page 1.
  const withReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const rangeInvalid = Boolean(dateMode === "range" && from && to && to < from);

  const fetchHistory = useCallback(() => {
    if (rangeInvalid) return;
    const params = { page, per_page: DEFAULT_PAGE_SIZE };
    if (dateMode === "date" && date) params.date = date;
    if (dateMode === "range") {
      if (from) params.from = from;
      if (to) params.to = to;
    }
    if (search.trim()) params.search = search.trim();
    if (payableType) params.payable_type = payableType;

    setLoading(true);
    setError("");
    listShroffPaymentHistory(params)
      .then(({ rows: data, pagination: p }) => {
        setRows(data);
        setPagination(p);
      })
      .catch((err) => {
        setRows([]);
        setPagination({ page: 1, last_page: 1, total: 0 });
        setError(errInfo(err, "Could not load shroff payment history.").message);
      })
      .finally(() => setLoading(false));
  }, [page, dateMode, date, from, to, search, payableType, rangeInvalid]);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(fetchHistory, 300);
    return () => clearTimeout(t);
  }, [fetchHistory]);

  const handleViewReceipt = async (approvalId) => {
    if (viewingReceiptId) return;
    const tab = window.open("", "_blank");
    setViewingReceiptId(approvalId);
    try {
      const blob = await getManualPaymentReceiptFile(approvalId);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(errInfo(err, "Could not open this receipt.").message);
    } finally {
      setViewingReceiptId(null);
    }
  };

  const clearFilters = () => {
    setDateMode("all");
    setDate(todayStr());
    setFrom("");
    setTo("");
    setSearch("");
    setPayableType("");
    setPage(1);
  };

  const hasFilters = dateMode !== "all" || search.trim() || payableType;

  return (
    <div>
      <div className="bg-white rounded-2xl shadow-sm p-5 mb-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className={labelClass}>Search</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
              </span>
              <input
                className={`${inputClass} pl-9`}
                placeholder="Receipt reference or confirmed by…"
                value={search}
                onChange={(e) => withReset(setSearch)(e.target.value)}
                maxLength={100}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Type</label>
            <select
              className={`${inputClass} w-auto`}
              value={payableType}
              onChange={(e) => withReset(setPayableType)(e.target.value)}
            >
              <option value="">All types</option>
              {HISTORY_PAYABLE_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={labelClass}>Paid date</label>
            <select
              className={`${inputClass} w-auto`}
              value={dateMode}
              onChange={(e) => withReset(setDateMode)(e.target.value)}
            >
              {DATE_MODES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {dateMode === "date" && (
            <div>
              <label className={labelClass}>Date</label>
              <input
                type="date"
                className={`${inputClass} w-auto`}
                value={date}
                onChange={(e) => withReset(setDate)(e.target.value)}
              />
            </div>
          )}
          {dateMode === "range" && (
            <>
              <div>
                <label className={labelClass}>From</label>
                <input
                  type="date"
                  className={`${inputClass} w-auto`}
                  value={from}
                  max={to || undefined}
                  onChange={(e) => withReset(setFrom)(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>To</label>
                <input
                  type="date"
                  className={`${inputClass} w-auto`}
                  value={to}
                  min={from || undefined}
                  onChange={(e) => withReset(setTo)(e.target.value)}
                />
              </div>
            </>
          )}
          {hasFilters && (
            <button type="button" onClick={clearFilters} className={`${btnSecondary} !w-auto px-5`}>
              Clear filters
            </button>
          )}
        </div>
        {rangeInvalid && <p className="text-sm text-red-600">The "To" date must be on or after the "From" date.</p>}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 py-6 text-center">{error}</p>
        ) : rows.length === 0 ? (
          <EmptyState icon={ICONS.payments} text="No shroff payments match your filters." />
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-3">
              {pagination.total} payment{pagination.total === 1 ? "" : "s"}
            </p>
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-left min-w-[960px]">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-3 font-medium">Paid Date</th>
                    <th className="py-2 px-3 font-medium">Receipt Ref</th>
                    <th className="py-2 px-3 font-medium">Type</th>
                    <th className="py-2 px-3 font-medium">For</th>
                    <th className="py-2 px-3 font-medium">Payment Ref</th>
                    <th className="py-2 px-3 font-medium">Amount</th>
                    <th className="py-2 px-3 font-medium">Confirmed By</th>
                    {canViewReceipt && <th className="py-2 pl-3 font-medium text-right">Receipt</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const p = r.payable || {};
                    const name = p.member_name || p.club_name || "—";
                    const code = p.member_code || p.club_code;
                    return (
                      <tr key={r.approval_id} className="border-b border-gray-50 last:border-0 align-top">
                        <td className="py-3 pr-3 text-sm text-gray-600 whitespace-nowrap">{fmtDate(r.paid_date)}</td>
                        <td className="py-3 px-3 text-sm font-mono text-gray-600 whitespace-nowrap">
                          {r.receipt_reference || "—"}
                        </td>
                        <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">
                          {HISTORY_TYPE_LABELS[r.payment_for] || r.payment_for || "—"}
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                            {name}
                          </p>
                          {code && <p className="text-xs text-gray-400 font-mono">{code}</p>}
                        </td>
                        <td className="py-3 px-3 text-sm font-mono text-gray-600 whitespace-nowrap">
                          {r.payment_reference || "—"}
                        </td>
                        <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{money(r.amount_paid)}</td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <p className="text-sm text-gray-600">{r.submitted_by_name || "—"}</p>
                          {r.submitted_at && <p className="text-xs text-gray-400">{fmtDateTime(r.submitted_at)}</p>}
                        </td>
                        {canViewReceipt && (
                          <td className="py-3 pl-3 text-right whitespace-nowrap">
                            {r.has_receipt_file ? (
                              <button
                                type="button"
                                onClick={() => handleViewReceipt(r.approval_id)}
                                disabled={viewingReceiptId === r.approval_id}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50"
                              >
                                <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                                {viewingReceiptId === r.approval_id ? "Opening…" : "View"}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">None</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pagination.last_page > 1 && (
              <div className="flex justify-center pt-5 mt-1 border-t border-gray-100">
                <Pagination page={pagination.page} totalPages={pagination.last_page} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PageHeader({ title, subtitle, onBack }) {
  return (
    <div className="mb-5 flex items-center gap-4">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          title="Back"
          className="w-11 h-11 shrink-0 rounded-xl bg-blue-100 text-blue-600 hover:bg-blue-200 flex items-center justify-center transition"
        >
          <Icon path={DESK_ICONS.arrowLeft} className="w-5 h-5" width={2} />
        </button>
      )}
      <div>
        <h1 className={`${onBack ? "text-2xl" : "text-xl"} font-bold`} style={{ color: TOKENS.NAVY }}>
          {title}
        </h1>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
    </div>
  );
}

export function ShroffFindConfirmPage() {
  const navigate = useNavigate();
  // Back goes to the previous page when there is one, else the admin dashboard.
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/admin"));
  return (
    <div>
      <PageHeader
        title="Find & Confirm"
        subtitle="Look up a payment by reference number and record a shroff counter payment."
        onBack={goBack}
      />
      <ShroffDesk />
    </div>
  );
}

export function ShroffPaymentHistoryPage() {
  return (
    <div>
      <PageHeader title="Payment History" subtitle="Payments confirmed at the shroff counter." />
      <ShroffPaymentHistory />
    </div>
  );
}
